import React, { useState, useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Select } from '../ui/Select';
import NumberInput from '../ui/NumberInput';
import { useUserSettings, UserSettings } from '../../modules/settings/useData';
import api from '../../services/api';
import useSWR from 'swr';

const viewPreferencesFetcher = async (url: string) => {
  const response = await api.get(url);
  return response.data.data || {};
};

const moduleViewOptions: Record<string, Array<{ value: string; label: string }>> = {
  Tasks: [{ value: 'list', label: 'List View' }],
  'Support Tickets': [
    { value: 'list', label: 'List View' },
    { value: 'kanban', label: 'Kanban View' },
  ],
  Todos: [
    { value: 'list', label: 'List View' },
    { value: 'monthly', label: 'Monthly View' },
  ],
  Notes: [
    { value: 'list', label: 'List View' },
    { value: 'card', label: 'Cardboard View' },
  ],
  'Client Vault': [
    { value: 'list', label: 'List View' },
    { value: 'templates', label: 'Templates' },
  ],
  Emails: [
    { value: 'table', label: 'Table' },
    { value: 'card', label: 'Cards' },
  ],
  Calls: [
    { value: 'list', label: 'List View' },
    { value: 'card', label: 'Cardboard View' },
  ],
  Reminders: [
    { value: 'list', label: 'List View' },
    { value: 'card', label: 'Cardboard View' },
  ],
  Appointments: [
    { value: 'list', label: 'List View' },
    { value: 'calendar', label: 'Calendar View' },
  ],
  Leads: [{ value: 'list', label: 'List View' }],
  Expenses: [
    { value: 'list', label: 'List View' },
    { value: 'summary', label: 'Summary View' },
  ],
  Projects: [
    { value: 'list', label: 'List View' },
    { value: 'card', label: 'Cardboard View' },
  ],
  Packages: [
    { value: 'list', label: 'List View' },
    { value: 'card', label: 'Cardboard View' },
  ],
  'Support Agreements': [
    { value: 'list', label: 'List View' },
    { value: 'card', label: 'Cardboard View' },
  ],
};

interface ChoiceOption {
  value: string;
  label: string;
  hint?: string;
}

interface PreferenceSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const PreferenceSection: React.FC<PreferenceSectionProps> = ({ title, description, children }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border', colors.border)}>
      <div className={cn('border-b px-4 py-3 sm:px-5', colors.border, colors.headerBg)}>
        <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
        <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{description}</p>
      </div>
      <div className="space-y-5 p-4 sm:p-5">{children}</div>
    </section>
  );
};

interface ChoiceGroupProps {
  label: string;
  value: string;
  options: ChoiceOption[];
  onChange: (value: string) => void;
  columns?: 2 | 3;
}

const ChoiceGroup: React.FC<ChoiceGroupProps> = ({
  label,
  value,
  options,
  onChange,
  columns = 3,
}) => {
  const { colors } = useTheme();
  return (
    <div>
      <Label className="mb-2">{label}</Label>
      <div
        className={cn(
          'grid gap-2',
          columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'
        )}
      >
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                'rounded-xl border px-3 py-2.5 text-left transition-all',
                selected
                  ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500/30'
                  : cn(colors.border, colors.interactiveHover, colors.surface)
              )}
            >
              <span className={cn('block text-sm font-medium', selected ? 'text-teal-900' : colors.text)}>
                {option.label}
              </span>
              {option.hint && (
                <span className={cn('mt-0.5 block text-xs', selected ? 'text-teal-700/80' : colors.textMuted)}>
                  {option.hint}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Preferences: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { userSettings, isLoading, mutate: mutateUser } = useUserSettings();
  const { data: viewPreferences, mutate: mutateViewPreferences } = useSWR<Record<string, string>>(
    '/settings/user/view-preferences',
    viewPreferencesFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const [userFormData, setUserFormData] = useState<Partial<UserSettings>>({});
  const [viewPreferencesData, setViewPreferencesData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savingViews, setSavingViews] = useState(false);

  useEffect(() => {
    if (userSettings) {
      setUserFormData({
        defaultPageSize: userSettings.defaultPageSize,
        tableDensity: userSettings.tableDensity,
        dateFormat: userSettings.dateFormat || '',
        dateLocale: userSettings.dateLocale || '',
        theme: userSettings.theme || 'light',
        favoritesDisplayMode: userSettings.favoritesDisplayMode || 'sidebar',
        detailViewLayout: userSettings.detailViewLayout || 'card',
        editFormLayout: userSettings.editFormLayout || 'default',
      });
    }
  }, [userSettings]);

  const validViewTypes: Record<string, string[]> = {
    Tasks: ['list'],
    'Support Tickets': ['list', 'kanban'],
    Todos: ['list', 'monthly'],
    Notes: ['list', 'card'],
    'Client Vault': ['list', 'templates'],
    Emails: ['table', 'card'],
    Calls: ['list', 'card'],
    Reminders: ['list', 'card'],
    Appointments: ['list', 'calendar'],
    Leads: ['list'],
    Expenses: ['list', 'summary'],
    Projects: ['list', 'card'],
    Packages: ['list', 'card'],
    'Support Agreements': ['list', 'card'],
  };

  useEffect(() => {
    if (viewPreferences) {
      const validated: Record<string, string> = {};
      for (const [moduleName, options] of Object.entries(moduleViewOptions)) {
        const currentValue = viewPreferences[moduleName];
        if (currentValue && options.some((opt) => opt.value === currentValue)) {
          validated[moduleName] = currentValue;
        } else if (options.length > 0) {
          validated[moduleName] = options[0].value;
        }
      }
      setViewPreferencesData(validated);
    }
  }, [viewPreferences]);

  const handleUserSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.patch('/settings/user', userFormData);
      await mutateUser();
      showSuccess('Preferences updated successfully');
    } catch (error: any) {
      showError(error?.response?.data?.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleViewPreferencesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingViews(true);

    try {
      await api.patch('/settings/user/view-preferences', {
        preferences: viewPreferencesData,
      });
      await mutateViewPreferences();
      window.dispatchEvent(new CustomEvent('viewPreferencesUpdated'));
      showSuccess('View preferences updated successfully');
    } catch (error: any) {
      showError(error?.response?.data?.message || 'Failed to update view preferences');
    } finally {
      setSavingViews(false);
    }
  };

  const handleViewPreferenceChange = (moduleName: string, viewType: string) => {
    const validTypes = validViewTypes[moduleName];
    if (validTypes && validTypes.includes(viewType)) {
      setViewPreferencesData({
        ...viewPreferencesData,
        [moduleName]: viewType,
      });
    } else if (validTypes && validTypes.length > 0) {
      setViewPreferencesData({
        ...viewPreferencesData,
        [moduleName]: validTypes[0],
      });
    }
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center', embedded ? 'min-h-[12rem]' : 'min-h-screen')}>
        <div className={cn('text-sm', colors.textSecondary)}>Loading preferences...</div>
      </div>
    );
  }

  const content = (
    <div className="space-y-5">
      <form onSubmit={handleUserSettingsSubmit} className="space-y-5">
        <PreferenceSection title="Tables" description="Default page size and row spacing for lists.">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <Label htmlFor="defaultPageSize" required>
                Default page size
              </Label>
              <NumberInput
                id="defaultPageSize"
                name="defaultPageSize"
                value={userFormData.defaultPageSize || 200}
                onChange={(e) =>
                  setUserFormData({
                    ...userFormData,
                    defaultPageSize: (e.target.value as number) || 200,
                  })
                }
                min={10}
                max={1000}
                required
                className="mt-1.5"
              />
              <p className={cn('mt-1.5 text-xs', colors.textMuted)}>Between 10 and 1000 rows per page</p>
            </div>

            <ChoiceGroup
              label="Table density"
              value={userFormData.tableDensity || 'normal'}
              onChange={(value) =>
                setUserFormData({ ...userFormData, tableDensity: value as UserSettings['tableDensity'] })
              }
              options={[
                { value: 'compact', label: 'Compact', hint: 'More rows' },
                { value: 'normal', label: 'Normal', hint: 'Balanced' },
                { value: 'comfortable', label: 'Comfortable', hint: 'More space' },
              ]}
            />
          </div>
        </PreferenceSection>

        <PreferenceSection title="Date & locale" description="How dates are formatted across the app.">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <Label htmlFor="dateFormat">Date format</Label>
              <Select
                value={userFormData.dateFormat || ''}
                onChange={(e) => setUserFormData({ ...userFormData, dateFormat: e.target.value })}
                className="mt-1.5"
                options={[
                  { value: '', label: 'Use system default' },
                  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                ]}
              />
            </div>

            <div>
              <Label htmlFor="dateLocale">Date locale</Label>
              <Select
                value={userFormData.dateLocale || ''}
                onChange={(e) => setUserFormData({ ...userFormData, dateLocale: e.target.value })}
                className="mt-1.5"
                options={[
                  { value: '', label: 'Use system default' },
                  { value: 'en-GB', label: 'English (UK)' },
                  { value: 'en-US', label: 'English (US)' },
                  { value: 'de-DE', label: 'German' },
                  { value: 'fr-FR', label: 'French' },
                ]}
              />
            </div>
          </div>
        </PreferenceSection>

        <PreferenceSection title="Interface" description="Theme, favorites placement, and form layouts.">
          <ChoiceGroup
            label="Theme"
            value={userFormData.theme || 'light'}
            onChange={(value) => setUserFormData({ ...userFormData, theme: value as UserSettings['theme'] })}
            options={[
              { value: 'light', label: 'Light', hint: 'Bright surfaces' },
              { value: 'dark', label: 'Dark', hint: 'Low glare' },
              { value: 'auto', label: 'Auto', hint: 'Match system' },
            ]}
          />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <Label htmlFor="favoritesDisplayMode">Favorites display</Label>
              <Select
                value={userFormData.favoritesDisplayMode || 'sidebar'}
                onChange={(e) =>
                  setUserFormData({
                    ...userFormData,
                    favoritesDisplayMode: e.target.value as UserSettings['favoritesDisplayMode'],
                  })
                }
                className="mt-1.5"
                options={[
                  { value: 'sidebar', label: 'Sidebar (default)' },
                  { value: 'dropdown', label: 'Topbar dropdown' },
                  { value: 'bar', label: 'Topbar quick access bar' },
                  { value: 'pins', label: 'Topbar icon pins' },
                  { value: 'none', label: 'Hidden' },
                ]}
              />
            </div>

            <div>
              <Label htmlFor="detailViewLayout">Detail view layout</Label>
              <Select
                id="detailViewLayout"
                value={userFormData.detailViewLayout || 'card'}
                onChange={(e) =>
                  setUserFormData({
                    ...userFormData,
                    detailViewLayout: e.target.value as 'card' | 'compact' | 'minimal',
                  })
                }
                className="mt-1.5"
                options={[
                  { value: 'card', label: 'Card' },
                  { value: 'compact', label: 'Compact' },
                  { value: 'minimal', label: 'Minimal' },
                ]}
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="editFormLayout">Edit form layout</Label>
              <Select
                id="editFormLayout"
                value={userFormData.editFormLayout || 'default'}
                onChange={(e) =>
                  setUserFormData({
                    ...userFormData,
                    editFormLayout: e.target.value as 'default' | 'compact' | 'minimal',
                  })
                }
                className="mt-1.5"
                options={[
                  { value: 'default', label: 'Default' },
                  { value: 'compact', label: 'Compact' },
                  { value: 'minimal', label: 'Minimal (no section titles, labels in fields)' },
                ]}
              />
            </div>
          </div>
        </PreferenceSection>

        <div className={cn('flex justify-end border-t pt-4', colors.border)}>
          <Button type="submit" variant="default" size="default" loading={saving} disabled={saving}>
            Save preferences
          </Button>
        </div>
      </form>

      <form onSubmit={handleViewPreferencesSubmit}>
        <PreferenceSection
          title="Default module views"
          description="Choose the view that opens when you enter each module."
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Object.entries(moduleViewOptions).map(([moduleName, options]) => (
              <div
                key={moduleName}
                className={cn('rounded-lg border px-3 py-2.5', colors.border, colors.headerBg)}
              >
                <Label htmlFor={`view-${moduleName}`} className="text-xs uppercase tracking-wide">
                  {moduleName}
                </Label>
                <Select
                  id={`view-${moduleName}`}
                  value={viewPreferencesData[moduleName] || options[0].value}
                  onChange={(e) => handleViewPreferenceChange(moduleName, e.target.value)}
                  className="mt-1.5"
                  options={options}
                />
              </div>
            ))}
          </div>

          <div className={cn('flex justify-end border-t pt-4', colors.border)}>
            <Button type="submit" variant="default" size="default" loading={savingViews} disabled={savingViews}>
              Save view preferences
            </Button>
          </div>
        </PreferenceSection>
      </form>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <div>
          <h1 className={cn('text-xl font-semibold tracking-tight', colors.text)}>Preferences</h1>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            Personal display and navigation settings for your account.
          </p>
        </div>
        {content}
      </div>
    </div>
  );
};

export default Preferences;
